import { useMemo, type ReactNode } from 'react';
import cn from 'clsx';
import { getEventResult, type EventKol } from 'r/atoms';
import { SpinnerCircle } from 'r/ui';
import { formatHitRate, HitMissCounts } from '../shared';
import { useSanitizerIssuer } from './queries';

// Sanitizer returns ratios as strings ("0.34" = 34%)
const formatRatio = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  return Number.isFinite(num) ? `${(num * 100).toFixed(1)}%` : '—';
};

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
}) {
  return (
    <div className="bg-main rounded-xl border border-bg-accent-tabs p-4 flex flex-col gap-1">
      <span className="text-xs text-secondary uppercase tracking-wide">
        {label}
      </span>
      <span className="text-xl font-semibold text-main">{value}</span>
      {hint && <span className="text-xs text-secondary">{hint}</span>}
    </div>
  );
}

export default function KolStats({ kol }: { kol: EventKol }) {
  const issuerQuery = useSanitizerIssuer(kol.kolID);
  const issuer = issuerQuery.data;

  const bySymbol = useMemo(() => {
    const map = new Map<
      string,
      { count: number; hits: number; misses: number; up: number }
    >();
    for (const event of kol.events) {
      const row = map.get(event.symbol) ?? {
        count: 0,
        hits: 0,
        misses: 0,
        up: 0,
      };
      row.count++;
      if (event.isDirectionUp) row.up++;
      const result = getEventResult(event);
      if (result === 'hit') row.hits++;
      if (result === 'miss') row.misses++;
      map.set(event.symbol, row);
    }
    return [...map.entries()].sort(([, a], [, b]) => b.count - a.count);
  }, [kol.events]);

  const upCount = kol.events.filter((e) => e.isDirectionUp).length;

  const topTokens =
    issuer?.topTokens ?
      Object.entries(issuer.topTokens)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 8)
    : [];

  return (
    <div className="flex flex-col gap-8">
      <section className="flex flex-col gap-3">
        <h3 className="text-base font-semibold text-main">SanPlay events</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            label="Events"
            value={kol.eventsCount}
            hint={`${kol.activeCount} active · ${kol.resolvedCount} resolved`}
          />
          <StatCard
            label="Hit rate"
            value={formatHitRate(kol.hitRate)}
            hint="Hits / (hits + misses)"
          />
          <StatCard
            label="Hit / Miss / Neutral"
            value={
              <HitMissCounts
                hits={kol.hits}
                misses={kol.misses}
                neutrals={kol.neutrals}
              />
            }
            hint="Neutral = range outcome won"
          />
          <StatCard
            label="Up / Down calls"
            value={`${upCount} / ${kol.eventsCount - upCount}`}
          />
        </div>

        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-left text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
              <tr>
                <th className="py-2 px-4 font-medium">Symbol</th>
                <th className="py-2 px-4 font-medium">Events</th>
                <th className="py-2 px-4 font-medium">Up / Down</th>
                <th className="py-2 px-4 font-medium">Hits</th>
                <th className="py-2 px-4 font-medium">Misses</th>
              </tr>
            </thead>
            <tbody>
              {bySymbol.map(([symbol, row]) => (
                <tr key={symbol} className="border-b">
                  <td className="py-2 px-4 font-semibold">{symbol}</td>
                  <td className="py-2 px-4">{row.count}</td>
                  <td className="py-2 px-4">
                    {row.up} / {row.count - row.up}
                  </td>
                  <td className="py-2 px-4 text-green-500">{row.hits}</td>
                  <td className="py-2 px-4 text-red-500">{row.misses}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <h3 className="text-base font-semibold text-main">
          Sanitizer analytics
        </h3>
        {issuerQuery.isLoading ?
          <div className="flex justify-center p-6">
            <SpinnerCircle size={20} color="var(--muted-foreground)" />
          </div>
        : !issuer ?
          <p className="text-sm text-secondary">
            {issuerQuery.isError ?
              'Failed to load Sanitizer data'
            : 'Author is not tracked in Sanitizer'}
          </p>
        : <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard
                label="Predictions"
                value={issuer.predictionsCount ?? 0}
                hint={`${issuer.longCount ?? 0} long · ${issuer.shortCount ?? 0} short`}
              />
              <StatCard
                label="Win rate 1W / 1M"
                value={`${formatRatio(issuer.winRate_1w)} / ${formatRatio(issuer.winRate_1M)}`}
                hint={`${issuer.gainCount_1M ?? 0}/${issuer.completedCount_1M ?? 0} completed in 1M`}
              />
              <StatCard
                label="Avg performance 1M"
                value={
                  <span
                    className={cn(
                      Number(issuer.avgPerformance_1M) >= 0 ? 'text-green-500'
                      : 'text-red-500',
                    )}
                  >
                    {formatRatio(issuer.avgPerformance_1M)}
                  </span>
                }
                hint={`Best ${formatRatio(issuer.bestPerformance_1M)} · worst ${formatRatio(issuer.worstPerformance_1M)}`}
              />
              <StatCard
                label="Sanitizer KOL"
                value={issuer.adminInfo?.isKol ? 'Yes' : 'No'}
                hint={
                  issuer.adminInfo?.isKol ?
                    `Rank 1M: ${issuer.rank_1M || '—'}`
                  : 'Not on Sanitizer leaderboard'
                }
              />
            </div>
            {topTokens.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-secondary">Top tokens:</span>
                {topTokens.map(([token, count]) => (
                  <span
                    key={token}
                    className="px-2 py-1 bg-bg-accent-main rounded-md border border-bg-accent-tabs font-medium"
                  >
                    {token} · {count}
                  </span>
                ))}
              </div>
            )}
            {issuer.adminInfo?.insight && (
              <p className="text-sm text-main italic">
                “{issuer.adminInfo.insight}”
              </p>
            )}
          </>
        }
      </section>
    </div>
  );
}
